import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { applyBrandOverrides, applyTenantTheme } from '../lib/utils.js';
import { resolveFontStack } from '../lib/design-options.js';

const TenantContext = createContext(null);

export const DEFAULT_FALLBACK_TENANT = {
  id: 'ten_demo_partner',
  slug: 'demo-partner',
  domain: 'localhost:8789',
  name: 'Partner-Shop',
  logoUrl: '/assets/logos/asl-logo.svg',
  primaryColor: '#e3000b',
  secondaryColor: '#5f5e5f',
  contactEmail: '',
  phone: '',
  address: null,
  imprint: '',
  privacyPolicy: '',
  currency: 'EUR',
  vatRate: 0.19,
  isActive: true,
  settings: {
    theme_preset: 'astryx-neutral',
    layout_header: 'slim',
    layout_hero: 'portal_search',
    product_card_variant: 'specs_heavy',
    filter_layout: 'sidebar',
    checkout_mode: 'both',
    price_display: 'net',
    theme_radius: '0.375rem',
    font_family: 'Hanken Grotesk, system-ui, sans-serif',
    branding: {
      slogan: 'Ihr Fachbetrieb für Sicherheitstechnik',
      shortName: 'Partner-Shop',
      heroBadge: 'Fachpartner',
      heroHeadline: 'Professionelle Sicherheitstechnik.',
      heroSubheadline: 'Ihr Fachpartner für Gefahrenmelde-, Video-, Brand- und Zutrittssysteme.',
    },
  },
};

export function normalizeTenant(raw) {
  if (!raw || typeof raw !== 'object') return DEFAULT_FALLBACK_TENANT;

  let settings = {};
  if (typeof raw.settings === 'string') {
    try {
      settings = JSON.parse(raw.settings);
    } catch {
      settings = {};
    }
  } else if (typeof raw.settings === 'object' && raw.settings !== null) {
    settings = raw.settings;
  }

  let address = raw.address;
  if (typeof address === 'string') {
    try {
      address = JSON.parse(address);
    } catch {
      address = raw.address;
    }
  }

  const primaryColor = raw.primaryColor || raw.primary_color || '#e3000b';
  const secondaryColor = raw.secondaryColor || raw.secondary_color || '#5f5e5f';
  const contactEmail = raw.contactEmail || raw.contact_email || '';
  const logoUrl = raw.logoUrl || raw.logo_url || '';
  const phone = raw.phone || '';
  const imprint = raw.imprint || '';
  const privacyPolicy = raw.privacyPolicy || raw.privacy_policy || '';
  const isActive = raw.isActive !== undefined ? Boolean(raw.isActive) : (raw.is_active !== undefined ? Boolean(raw.is_active) : true);

  return {
    ...raw,
    id: raw.id || '',
    slug: raw.slug || '',
    domain: raw.domain || '',
    name: raw.name || '',
    primaryColor,
    secondaryColor,
    contactEmail,
    logoUrl,
    phone,
    address: address || null,
    imprint,
    privacyPolicy,
    currency: raw.currency || 'EUR',
    vatRate: raw.vatRate !== undefined ? raw.vatRate : 0.19,
    isActive,
    settings: {
      theme_preset: settings.theme_preset || settings.themePreset || raw.theme_preset || raw.themePreset || 'astryx-neutral',
      layout_header: settings.layout_header || settings.layoutHeader || raw.layout_header || raw.layoutHeader || 'slim',
      layout_hero: settings.layout_hero || settings.layoutHero || raw.layout_hero || raw.layoutHero || 'portal_search',
      product_card_variant: settings.product_card_variant || settings.productCardVariant || raw.product_card_variant || raw.productCardVariant || 'specs_heavy',
      filter_layout: settings.filter_layout || settings.filterLayout || raw.filter_layout || raw.filterLayout || 'sidebar',
      theme_radius: settings.theme_radius || settings.themeRadius || raw.theme_radius || raw.themeRadius || '0.375rem',
      branding: {
        ...(typeof settings.branding === 'object' && settings.branding !== null ? settings.branding : {}),
      },
      ...settings,
    },
  };
}

// Theme-Payload für applyTenantTheme. Schriften laufen über die kuratierte
// Liste (design-options.js): nur bekannte IDs/Altwerte werden zu einem Stack.
function themeFromTenant(tenant) {
  return {
    primaryColor: tenant.primaryColor,
    secondaryColor: tenant.secondaryColor,
    borderRadius: tenant.settings?.theme_radius || '0.375rem',
    fontFamily: resolveFontStack(tenant.settings?.font_family) || undefined,
    fontHeading: resolveFontStack(tenant.settings?.font_heading) || undefined,
  };
}

export function updateDocumentMetadata(tenant) {
  if (typeof document === 'undefined' || !tenant) return;

  const slogan = tenant.settings?.branding?.slogan || 'Fachshop für Sicherheitstechnik';
  document.title = tenant.name ? `${tenant.name} | ${slogan}` : 'Partner-Shop';

  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.name = 'description';
    document.head.appendChild(metaDesc);
  }
  metaDesc.content = `${tenant.name} – ${slogan}.`;

  if (tenant.settings?.branding?.faviconUrl || tenant.logoUrl) {
    let favicon = document.querySelector('link[rel="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = tenant.settings?.branding?.faviconUrl || tenant.logoUrl;
  }
}

export function TenantProvider({ children, initialTenant = null }) {
  const normalizedInitial = useMemo(() => initialTenant ? normalizeTenant(initialTenant) : null, [initialTenant]);
  const [tenant, setTenant] = useState(normalizedInitial || null);
  const [loading, setLoading] = useState(!normalizedInitial);
  const [error, setError] = useState(null);

  const resolveTenant = useCallback(async () => {
    if (normalizedInitial) {
      applyTenantTheme(themeFromTenant(normalizedInitial));
      updateDocumentMetadata(normalizedInitial);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (typeof window === 'undefined') {
        setTenant(null);
        setLoading(false);
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const queryTenant = urlParams.get('tenant') || urlParams.get('tenant_id') || urlParams.get('domain');
      const host = window.location.host;

      // Self-Healing (Issue #3): Während eines Pages-Deploys antwortet die API
      // kurz mit 5xx. Der Shop soll das abfedern — bis zu 3 Versuche mit
      // Backoff, ehe der Fehler-Screen steht. Bei explizitem ?tenant=-Slug,
      // der scheitert, wird einmalig domain-only nachgelöst.
      let resolveUrl = '/api/tenant/resolve';
      if (queryTenant) {
        resolveUrl += `?tenant=${encodeURIComponent(queryTenant)}`;
      } else {
        resolveUrl += `?domain=${encodeURIComponent(host)}`;
      }

      const RETRY_DELAYS = [800, 1600, 2400];
      let res = null;
      let lastStatus = 0;
      for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
        let caughtErr = null;
        try {
          // cache: 'no-cache' — die Antwort ist 60 s browser-cachebar; ohne Revalidierung
          // würde ein Reload direkt nach einer Design-Änderung (Paket B) den alten
          // Stand zeigen.
          res = await fetch(resolveUrl, { headers: { 'Accept': 'application/json' }, cache: 'no-cache' });
        } catch (e) {
          caughtErr = e;
          res = null;
        }
        if (res && res.ok) break;
        lastStatus = res ? res.status : 0;
        if (attempt < RETRY_DELAYS.length) {
          await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
          if (queryTenant && attempt === RETRY_DELAYS.length - 1) {
            // letzter Versuch: domain-only (Server löst Subdomain auf)
            resolveUrl = `/api/tenant/resolve?domain=${encodeURIComponent(host)}`;
          }
        }
      }
      if (!res || !res.ok) {
        if (host.includes('localhost') || host.includes('127.0.0.1')) {
          const merged = normalizeTenant(DEFAULT_FALLBACK_TENANT);
          setTenant(merged);
          setError(null);
          applyTenantTheme(themeFromTenant(merged));
          updateDocumentMetadata(merged);
          return;
        }
        throw new Error(`Failed to resolve tenant: ${lastStatus || 'network'}`);
      }

      const data = await res.json();
      const resolvedTenant = data.ok ? (data.tenant || data) : (data.tenant || (data.id ? data : null));
      if (resolvedTenant && resolvedTenant.id) {
        const merged = normalizeTenant(resolvedTenant);
        setTenant(merged);
        setError(null);
        applyTenantTheme(themeFromTenant(merged));
        updateDocumentMetadata(merged);
      } else {
        if (host.includes('localhost') || host.includes('127.0.0.1')) {
          const merged = normalizeTenant(DEFAULT_FALLBACK_TENANT);
          setTenant(merged);
          setError(null);
          applyTenantTheme(themeFromTenant(merged));
          updateDocumentMetadata(merged);
          return;
        }
        throw new Error('Invalid tenant payload');
      }
    } catch (err) {
      if (typeof window !== 'undefined' && (window.location.host.includes('localhost') || window.location.host.includes('127.0.0.1'))) {
        const merged = normalizeTenant(DEFAULT_FALLBACK_TENANT);
        setTenant(merged);
        setError(null);
        applyTenantTheme(themeFromTenant(merged));
        updateDocumentMetadata(merged);
        return;
      }
      console.warn('Tenant resolution notice:', err.message);
      setError(err.message || 'Tenant context unavailable');
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, [normalizedInitial]);

  useEffect(() => {
    resolveTenant();
  }, [resolveTenant]);

  // Design-Drawer (Issue #3 Paket B): Nach jeder gespeicherten Design-Änderung
  // (und beim ersten Laden, idempotent zu applyTenantTheme) die Marken-Variablen
  // neu anwenden. Primitive Deps -> läuft nur, wenn sich Farbe/Schrift ändern.
  const tenantId = tenant?.id;
  const designPrimary = tenant?.primaryColor;
  const designSecondary = tenant?.secondaryColor;
  const designFont = tenant?.settings?.font_family;
  const designHeading = tenant?.settings?.font_heading;
  useEffect(() => {
    if (!tenantId) return;
    applyBrandOverrides({
      primaryColor: designPrimary,
      secondaryColor: designSecondary,
      fontFamily: designFont,
      fontHeading: designHeading,
    });
  }, [tenantId, designPrimary, designSecondary, designFont, designHeading]);

  // Übernimmt eine gespeicherte Design-Änderung in den Tenant-State (ohne
  // Refetch). `null` bei einer Schrift entfernt die Einstellung (= Standard).
  const applyDesign = useCallback((design) => {
    if (!design || typeof design !== 'object') return;
    setTenant((prev) => {
      if (!prev) return prev;
      const next = { ...prev, settings: { ...(prev.settings || {}) } };
      if (design.primaryColor) next.primaryColor = design.primaryColor;
      if (design.secondaryColor) next.secondaryColor = design.secondaryColor;
      for (const [field, key] of [['fontFamily', 'font_family'], ['fontHeading', 'font_heading']]) {
        if (!(field in design)) continue;
        if (design[field]) next.settings[key] = design[field];
        else delete next.settings[key];
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({
    tenant,
    loading,
    error,
    refetch: resolveTenant,
    isLoaded: !loading && Boolean(tenant?.id),
    setTenant,
    applyDesign,
  }), [tenant, loading, error, resolveTenant, applyDesign]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return ctx;
}
